import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { BaseService } from "../../core/base.service";

/**
 * 一生一档时间线条目
 */
export interface TimelineEntry {
  /** 日期 (YYYY-MM-DD) */
  date: string;
  /** 事件标题 */
  title: string;
  /** 事件详情 */
  detail?: string;
  /** 事件类型 */
  type:
    | "enrollment"
    | "grade"
    | "award_bonus"
    | "award_deduct"
    | "award"
    | "violation"
    | "leave"
    | "score"
    | "trace"
    | "honor"
    | "other";
}

/**
 * 学生实体（对应 MySQL student 表）
 */
export interface Student {
  /** 主键ID */
  id: number;
  /** 学生姓名 */
  name: string;
  /** 学号 */
  studentNo: string;
  /** 性别: 0=未知, 1=男, 2=女 */
  gender: number;
  /** 出生日期 (ISO 8601 string from backend) */
  birthDate?: string;
  /** 手机号码 */
  phone?: string;
  /** 邮箱地址 */
  email?: string;
  /** 身份证号 */
  idCard?: string;
  /** 年级 */
  grade?: string;
  /** 班级名称 */
  className?: string;
  /** 家庭住址 */
  address?: string;
  /** 父亲姓名 */
  fatherName?: string;
  /** 父亲电话 */
  fatherPhone?: string;
  /** 母亲姓名 */
  motherName?: string;
  /** 母亲电话 */
  motherPhone?: string;
  /** 日常联系人姓名 */
  dailyContactName?: string;
  /** 日常联系人电话 */
  dailyContactPhone?: string;
  /** 家庭情况(文本): "正常"=正常, "单亲", "重组", "留守", "孤儿"（历史数据可能为 "0"~"4"） */
  familySituation?: string;
  /** 身体状况(文本): "健康", "疾病", "特殊情况"（历史数据可能为 "0"~"2"） */
  healthStatus?: string;
  /** 特殊情况描述（当 healthStatus="特殊情况" 时填写） */
  healthStatusRemark?: string;
  /** 学生备注（最大 200 字符） */
  remarks?: string;
  /** 身高（cm），供智能排座使用 */
  height?: number | null;
  /** 性格标签，供智能排座使用 */
  personality?: string | null;
  /** 量化积分 */
  score?: number;
  /** 本月请假次数 */
  leaveCount?: number;
  /** 违纪次数 */
  violationCount?: number;
  /** 获奖次数 */
  awardCount?: number;
  /** 一生一档时间线 */
  timeline?: TimelineEntry[];
  /** 状态: 0=停用, 1=启用 */
  status: number;
  /** 创建时间 */
  addTime?: string;
  /** 创建人ID */
  addUserId?: number;
  /** 更新时间 */
  updateTime?: string;
  /** 更新人ID */
  updateUserId?: number;
  /** 头像 URL（可选） */
  avatar?: string | null;
  teacherId?: number;
  semesterId?: number;
}

/** 学生统计数据 */
export interface StudentStats {
  /** 量化积分 */
  totalScore: number;
  /** 累计加分 */
  totalBonus: number;
  /** 累计扣分 */
  totalDeduction: number;
  /** 本月请假次数 */
  leaveCount: number;
  /** 违纪次数 */
  violationCount: number;
  /** 获奖/加分次数 */
  awardCount: number;
}

@Injectable({ providedIn: "root" })
export class StudentService extends BaseService<Student> {
  constructor() {
    super("student");
  }

  /** 获取学生详情聚合数据（一次请求返回基本信息 + 统计 + 违纪/请假/成绩/分班历史/获奖） */
  getDetail(studentId: number): Observable<{
    student: Student;
    stats: StudentStats;
    violations: any[];
    leaves: any[];
    scores: any[];
    classHistories: any[];
    awards: any[];
    awardRecords: any[];
  }> {
    return this.http.get<any>(`${this.apiBaseUrl}/${this.apiPrefix}/Detail`, {
      params: { studentId },
    });
  }

  /** 获取学生统计数据 */
  getStats(studentId: number): Observable<StudentStats> {
    return this.http.get<StudentStats>(
      `${this.apiBaseUrl}/${this.apiPrefix}/GetStats`,
      { params: { studentId } },
    );
  }

  /** 批量改班（不记录换班历史） */
  batchTransfer(
    updates: Partial<Student>[],
  ): Observable<Student[]> {
    const url = `${this.apiBaseUrl}/${this.apiPrefix}/put?skipHistory=true`;
    return this.http
      .put<Student[]>(url, updates)
      .pipe(map((res) => this.unwrap<Student[]>(res)));
  }

  /** 导出学生档案为 PDF */
  exportPdf(studentId: number): Observable<Blob> {
    return this.http.get(`${this.apiBaseUrl}/${this.apiPrefix}/ExportPdf`, {
      params: { studentId },
      responseType: "blob",
    });
  }

  /** 导出学生档案为 Word (.docx)，包含留痕图片 */
  exportWord(studentId: number): Observable<Blob> {
    return this.http.get(`${this.apiBaseUrl}/${this.apiPrefix}/ExportWord`, {
      params: { studentId },
      responseType: "blob",
    });
  }

  /** 导出学生档案为 Excel (.xlsx)，多 Sheet 含图片/录音链接 */
  exportExcel(studentId: number): Observable<Blob> {
    return this.http.get(`${this.apiBaseUrl}/${this.apiPrefix}/ExportExcel`, {
      params: { studentId },
      responseType: "blob",
    });
  }

  /** 获取学生列表（含积分汇总） */
  getWithScores(
    params: {
      semesterId?: number;
      teacherId?: number;
    } = {},
  ): Observable<StudentWithScore[]> {
    let httpParams = new HttpParams();
    if (params.semesterId)
      httpParams = httpParams.set("semesterId", String(params.semesterId));
    if (params.teacherId)
      httpParams = httpParams.set("teacherId", String(params.teacherId));
    return this.http
      .get<
        StudentWithScore[]
      >(`${this.apiBaseUrl}/${this.apiPrefix}/getWithScores`, { params: httpParams })
      .pipe(map((res) => this.unwrap<StudentWithScore[]>(res)));
  }

  /** 学生列表页专用轻量接口：仅返回列表所需字段（id/name/studentNo/grade/className） */
  getListLight(params: { teacherId: number; semesterId?: number }): Observable<Student[]> {
    let httpParams = new HttpParams().set("teacherId", String(params.teacherId));
    if (params.semesterId)
      httpParams = httpParams.set("semesterId", String(params.semesterId));
    return this.http
      .get<Student[]>(`${this.apiBaseUrl}/${this.apiPrefix}/list`, {
        params: httpParams,
      })
      .pipe(map((res) => this.unwrap<Student[]>(res)));
  }

  /** 公开链接：获取某老师已有学生的年级/班级列表（无需登录，供新生登记下拉选择） */
  publicClasses(teacherId: number): Observable<{ classPairs: string[] }> {
    return this.http.get<any>(`${this.apiBaseUrl}/${this.apiPrefix}/PublicClasses`, {
      params: { teacherId },
    });
  }

  /** 公开链接：家长登记新学生（无需登录）
   * teacherId / semesterId 由老师生成链接时携带，
   * 后端用于归属 teacher_id / add_user_id / semester_id */
  publicAdd(data: {
    teacherId: number;
    semesterId: number;
    name: string;
    gender: number;
    grade: string;
    className: string;
    birthDate: string;
    phone: string;
    email: string;
    idCard: string;
    address: string;
    fatherName: string;
    fatherPhone: string;
    motherName: string;
    motherPhone: string;
    dailyContactName: string;
    dailyContactPhone: string;
    /** 家庭情况：存选中文本（"正常"/"单亲"/"重组"/"留守"/"孤儿"） */
    familySituation: string;
    /** 身体状况：存选中文本（"健康"/"疾病"/"特殊情况"） */
    healthStatus: string;
    healthStatusRemark: string;
    remarks: string;
  }): Observable<any> {
    return this.http.post<any>(
      `${this.apiBaseUrl}/${this.apiPrefix}/PublicAdd`,
      data,
    );
  }
}

/** 带积分的学生信息 */
export interface StudentWithScore {
  id: number;
  name: string;
  className: string;
  grade: string;
  score: number;
}
